import { FC } from 'react';
import { createPortal } from 'react-dom';
import { InlineNotification, Modal, TextInput, Toggle } from '@carbon/react';
import { FLAG_OVERRIDE_MODAL_SELECTOR } from 'components/flag-override-modal';
import { SharedAnchorNotice } from './shared-anchor-notice';
import type { SharedWith } from './types';
import style from './party-detail.module.scss';

interface Props {
  open: boolean;
  // The party's current anchor, which Mark as Correct resolves.
  anchorName: string;
  pending: boolean;
  hasUnresolvedAddressFlag: boolean;
  sharedWith?: SharedWith;
  // Mark as Correct deduplicates by default, even when the names differ.
  // Preserving keeps the staged record alive and aliases it to the match
  // instead — a deliberate choice that requires a written reason.
  preserveStaged: boolean;
  aliasReason: string;
  // A failed submit, kept beside the examiner's input for a retry.
  error?: string | null;
  onPreserveStagedChange: (preserve: boolean) => void;
  onAliasReasonChange: (reason: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}

export const MarkCorrectModal: FC<Props> = ({
  open,
  anchorName,
  pending,
  hasUnresolvedAddressFlag,
  sharedWith,
  preserveStaged,
  aliasReason,
  error,
  onPreserveStagedChange,
  onAliasReasonChange,
  onSubmit,
  onClose,
}) => {
  const aliasReasonMissing = preserveStaged && aliasReason.trim() === '';

  // Portaled so it stacks above an open side panel (same z-index, later in DOM).
  return createPortal(
    <Modal
      open={open}
      danger={false}
      modalHeading="Mark as Correct"
      primaryButtonText="Mark as Correct"
      secondaryButtonText="Cancel"
      onRequestSubmit={onSubmit}
      onRequestClose={onClose}
      primaryButtonDisabled={
        pending || hasUnresolvedAddressFlag || aliasReasonMissing
      }
      // Let the consolidation-risk override modal (portaled to <body>) hold focus.
      selectorsFloatingMenus={[FLAG_OVERRIDE_MODAL_SELECTOR]}
    >
      {error && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Error"
          subtitle={error}
          className={style.confirmNotification}
        />
      )}
      {hasUnresolvedAddressFlag && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title=""
          subtitle="Please resolve the address review flag before confirming the match."
          className={style.confirmNotification}
        />
      )}
      <SharedAnchorNotice sharedWith={sharedWith} />
      <p>
        {preserveStaged
          ? `“${anchorName}” will stay a separate record, aliased to the selected match.`
          : `“${anchorName}” will be deduplicated into the selected match.`}
      </p>
      {/* Blank labels mirror the reconciler's survivor toggle; the consequence
          is stated once below. */}
      <Toggle
        id="mark-correct-preserve"
        size="sm"
        labelText="Preserve as a separate entity"
        aria-label={`Preserve "${anchorName}" as a separate entity and alias it to the selected match`}
        labelA=""
        labelB=""
        toggled={preserveStaged}
        onToggle={(next: boolean) => onPreserveStagedChange(next)}
      />
      {preserveStaged && (
        <>
          <p className={style.confirmPreserveHelp}>
            This record stays live and is aliased to the match instead of being
            deduplicated into it.
          </p>
          <TextInput
            id="mark-correct-alias-reason"
            size="sm"
            labelText="Why keep this record separate? (required)"
            placeholder="Explain why these should be aliased instead of deduplicated"
            value={aliasReason}
            invalid={aliasReasonMissing}
            invalidText="An explanation is required to preserve a record as a separate entity."
            onChange={(e) => onAliasReasonChange(e.target.value)}
          />
        </>
      )}
    </Modal>,
    document.body
  );
};
